import { FormClassRetire, FormKbn } from "../assets/data/Constants";
import type {
  IAPI_RET005_Request,
  IAPI_RET018_Request,
  IAPI_STA008_Request,
} from "../type/apiType";
import type {
  IJob_32Props,
  sixMonthsSalaryBeforeApplyDetailList,
  sixMonthsSalaryBeforeApplyList,
} from "../type/contextType/businessContext/IJob_32";
import { IJob_35Props } from "../type/contextType/businessContext/IJob_35";
import { IJob_40Props } from "../type/contextType/businessContext/IJob_40";
import { IJob_41Props } from "../type/contextType/businessContext/IJob_41";
import { toNumberOrUndefined } from "../utils/useTools";

const monthsList = [
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "jan",
  "feb",
  "mar",
];

type TCombinedType<T1, T2> = T1 & T2;

// undefined除去
function removeUndefinedProperties<T, U>(obj: TCombinedType<T, U>) {
  const seen = new WeakMap<any, any>();

  function inner(value: any): any {
    if (value === undefined) return undefined;
    if (value === null) return null;

    if (value instanceof Date) return value.toISOString();

    const t = typeof value;
    if (t !== "object") return value; // number/string/boolean

    if (seen.has(value)) return seen.get(value);

    const result: any = Array.isArray(value) ? [] : {};
    seen.set(value, result);

    for (const key of Object.keys(value)) {
      const v = value[key];
      if (v === undefined) continue; // undefined を除去
      const copied = inner(v);
      if (copied !== undefined) result[key] = copied;
    }

    return result;
}

  return inner(obj) as TCombinedType<T, U>;
}

// 値の代入
function assignValuesFromObject(target: any, source: any) {
  Object.keys(target).forEach((key) => {
    if (source.hasOwnProperty(key)) {
      target[key] = source[key] !== "" ? source[key] : null;
    }
  });
  return target;
}

// 退職者詳細情報更新
function API_STA008_BuildReqParams(
  param: TCombinedType<IAPI_STA008_Request, IJob_32Props>
): IAPI_STA008_Request {
  const tempParam = removeUndefinedProperties<
    IAPI_STA008_Request,
    IJob_32Props
  >(param);

  // APIとBusinessContextの構成や物理名が異なる場合は、変換処理を実装する
  if (
    tempParam.sixMonthsSalaryBeforeApplyList?.length &&
    tempParam.sixMonthsSalaryBeforeApplyList.length > 0
  ) {
    tempParam.sixMonthsSalaryBeforeApplyList =
      tempParam.sixMonthsSalaryBeforeApplyList.map(
        (item: sixMonthsSalaryBeforeApplyList) => ({
          yearMonth: item.yearMonth || 0,
          salaryTableAmt: item.salaryTableAmt || 0,
          salaryAdjustmentAmt: item.salaryAdjustmentAmt || 0,
        })
      );
  }

  if (
    tempParam.sixMonthsSalaryBeforeApplyDetailList?.length &&
    tempParam.sixMonthsSalaryBeforeApplyDetailList.length > 0
  ) {
    tempParam.sixMonthsSalaryBeforeApplyDetailList =
      tempParam.sixMonthsSalaryBeforeApplyDetailList.map(
        (item: sixMonthsSalaryBeforeApplyDetailList) => ({
          yearMonth: item.yearMonth || 0,
          allowanceHisSerialNo: item.allowanceHisSerialNo || 0,
          adjustmentAmt: item.adjustmentAmt || 0,
        })
      );
  }

  if (tempParam.formList?.length && tempParam.formList.length > 0) {
    tempParam.formList = tempParam.formList.map((item: any) => {
      const { attachDocRegStatus, formName, ...rest } = item; //持回り情報のファイルのデータ
      const { formStatus, fileName } = item; // 画面で添付したファイルのデータ
      return {
        ...rest,
        formStatus: attachDocRegStatus ?? formStatus,
        fileName: formName ?? fileName,
      };
    });
  }

  // 退職年度のならない月転換
  // "0"(在職中)または"2"(非活性の在職中)の場合 = "0"(在職中)
  // "1"(職員ならない)または"3"(非活性の職員ならない)の場合 = "1"(職員ならない)

  function isKeyOfTempParam(
    key: any,
    obj: TCombinedType<IAPI_STA008_Request, IJob_32Props>
  ): key is keyof TCombinedType<IAPI_STA008_Request, IJob_32Props> {
    return key in obj;
  }

  tempParam?.screenId !== "SCRC060122" &&
    Object.keys(tempParam).forEach((key) => {
      if (monthsList.includes(key) && isKeyOfTempParam(key, tempParam)) {
        (tempParam as any)[key] = ["1", "3"].includes((tempParam as any)[key])
          ? "1"
          : "0";
      }
    });

  // 勤続年数 String => Number
  tempParam.page1LengthOfService !== undefined &&
    (tempParam.page1LengthOfService = toNumberOrUndefined(
      tempParam.page1LengthOfService
    ));
  tempParam.page2LengthOfService !== undefined &&
    (tempParam.page2LengthOfService = toNumberOrUndefined(
      tempParam.page2LengthOfService
    ));
  tempParam.page3LengthOfService !== undefined &&
    (tempParam.page3LengthOfService = toNumberOrUndefined(
      tempParam.page3LengthOfService
    ));

  const MapValue = {
    contractorNo: "",
    applyId: null,
    tempSaveStep: 0,
    businessType: "",
    staffNo: "",
    staffNameKanji: "",
    staffNameKana: "",
    BNameKanji: tempParam.bNameKanji || null,
    BNameKana: tempParam.bNameKana || null,
    birthday: "",
    sex: "",
    jobCd: "",
    retireReasonCd:
      tempParam.nRetireReasonCd || tempParam.fRetireReasonCd || "",
    retireProcedureReason: "",
    prefectureRetirementJoinFlg: "",
    retirementRequestOrderFlg: tempParam.retirementRequestOrder || "",
    firstJoinDate: "",
    changedName: "",
    changedNameKana: "",
    retireDate: "",
    zipcode: "",
    prefectureCd: "",
    addressCity: "",
    addressOthers: "",
    tel: "",
    year: 0,
    apr: "",
    may: "",
    jun: "",
    jul: "",
    aug: "",
    sep: "",
    oct: "",
    nov: "",
    dec: "",
    jan: "",
    feb: "",
    mar: "",
    sixMonthsSalaryBeforeApplyList: [],
    sixMonthsSalaryBeforeApplyDetailList: [],
    page1Kbn: "",
    page1PaymentAmt: 0,
    page1WithholdingAmt: 0,
    page1ScAmtCity: 0,
    page1ScAmtPrefecture: 0,
    page1LengthOfService: 0,
    page1EmployedDate: "",
    page1RetiredDate: "",
    page1DeductionPeriodYear: 0,
    page1DeductionPeriodMonth: 0,
    page2Kbn: "",
    page2PaymentAmt: 0,
    page2WithholdingAmt: 0,
    page2ScAmtCity: 0,
    page2ScAmtPrefecture: 0,
    page2LengthOfService: 0,
    page2EmployedDate: "",
    page2RetiredDate: "",
    page2DeductionPeriodYear: 0,
    page2DeductionPeriodMonth: 0,
    page3Kbn: "",
    page3PaymentAmt: 0,
    page3WithholdingAmt: 0,
    page3ScAmtCity: 0,
    page3ScAmtPrefecture: 0,
    page3LengthOfService: 0,
    page3EmployedDate: "",
    page3RetiredDate: "",
    page3DeductionPeriodYear: 0,
    page3DeductionPeriodMonth: 0,
    aContractorNo: "",
    aContractorName: tempParam.aCorpName || "",
    formList: [],
    formProspectNum: tempParam.withholdingTaxSlipTotal || 0,
    transferDate: "",
    continueTransferApplicable1Flg: "",
    continueTransferApplicable2Flg: "",
    workingDayNum: 0,
    receptionDate: "",
    contracterAllowanceRequestFlg: "",
    updateDateTime: "",
    screenId: "",
  };

  const finalRes = assignValuesFromObject(MapValue, tempParam);

  // 画面IDがSCRC060026の場合、tempSaveStepを削除
  if (tempParam.screenId === "SCRC060026") {
    delete finalRes.tempSaveStep;
  }

  // 画面IDがSCRC060026またはSCRC060028の場合、applyIdを削除
  if (
    (tempParam.screenId === "SCRC060026" ||
      tempParam.screenId === "SCRC060028") &&
    !finalRes.applyId
  ) {
    finalRes.applyId = null;
  }

  return finalRes as IAPI_STA008_Request;
}

// 退職手当金請求更新
function API_RET005_BuildReqParams(
  param: TCombinedType<IAPI_RET005_Request, IJob_35Props | IJob_41Props>
): IAPI_RET005_Request {
  const tempParam = removeUndefinedProperties<
    IAPI_RET005_Request,
    IJob_35Props | IJob_41Props
  >(param);

  if (tempParam.fileList) {
    tempParam.fileList = tempParam.fileList
      .filter(
        (item: any) =>
          item !== null &&
          item !== undefined &&
          !(
            item.formKbn === FormKbn["退職書類"] &&
            (item.formClass ===
              FormClassRetire["退職所得の源泉徴収票_退職届1枚目"] ||
              item.formClass ===
                FormClassRetire["退職所得の源泉徴収票_退職届2枚目"] ||
              item.formClass ===
                FormClassRetire["退職所得の源泉徴収票_退職届3枚目"])
          )
      )
      .map((item: any, index: number) => {
        return {
          serialNo: item.serialNo || index + 1,
          facilityNo: item.facilityNo,
          staffNo: item.staffNo || item.fileList_staffNo || item.staffNoForFile,
          formKbn: item.formKbn,
          formClass: item.formClass,
          fileName: item.fileName || item.formName,
          fileBase64: item.fileBase64 || item.file,
          issueDocumentNo: item.issueDocumentNo,
          issueDate: item.issueDate,
        };
      });
  }

  const MapValue = {
    applyId: null,
    tempSaveStep: null,
    businessType: null,
    contractorNo: null,
    staffNo: null,
    demanderName: null,
    demanderNameKana: null,
    demanderKbn: null,
    demanderRelationship: null,
    demanderRelationshipOthers: null,
    demanderBirth: null,
    tel: null,
    demanderEmail: null,
    agentEmail: null,
    demanderZipcode: null,
    demanderPrefectureCd: null,
    demanderCity: null,
    demanderAddressOthers: null,
    nowZipcode: null,
    nowPrefectureCd: null,
    nowAddressCity: null,
    nowAddressOthers: null,
    domesticResidenceFlg: null,
    retirementYearFirstZipcode: null,
    retirementYearFirstPrefectureCd: null,
    retirementYearFirstAddressCity: null,
    retirementYearAddressOthers: null,
    retirementReason: null,
    nRetireKbn: tempParam.retireKbn || null,
    firstJoinDate: null,
    retireDate: null,
    livingAssistanceFlg: null,
    paymentConfirmDate: null,
    demanderFlg: null,
    bankCd: null,
    bankName: null,
    branchCd: null,
    branchName: null,
    accountNameKana: null,
    depositType: null,
    accountNo: null,
    livelihoodFromRetireeFlg: null,
    identificationDocsKind: null,
    addPaymentApplyFlg: null,
    addPaymentReason: null,
    addPaymentAmt: null,
    totalPaymentAmtAfterAdd: null,
    pastLosInCurrentYearTotalFrom: tempParam.totalYearLosFrom || null,
    pastLosInCurrentYearTotalTo: tempParam.totalYearLosTo || null,
    pastLosInCurrentYearOtherFrom: tempParam.totalCurrentYearLosFrom || null,
    pastLosInCurrentYearOtherTo: tempParam.totalCurrentYearLosTo || null,
    currentYearLosFrom: null,
    currentYearLosTo: null,
    currentYearPaymentDecisionDate: null,
    currentYearRetirementAllowanceAmt: null,
    currentYearWithholdingAmt: null,
    currentYearScCityAmt: null,
    currentYearScPrefectureAmt: null,
    currentYearGetPaidDate: null,
    currentYearRetirementKbn: null,
    currentYearPayerZipcode: null,
    currentYearPayerPrefectureCd: null,
    currentYearPayerAddressCity: null,
    currentYearPayerAddressOthers: null,
    currentYearPayerName: null,
    past4yearsLosFrom: null,
    past4yearsLosTo: null,
    past4yearsPaymentDecisionDate: null,
    past4yearsRetirementAllowanceAmt: null,
    past4yearsWithholdingAmt: null,
    past4yearsScCityAmt: null,
    past4yearsScPrefectureAmt: null,
    past4yearsGetPaidDate: null,
    past4yearsRetirementKbn: null,
    past4yearsPayerZipcode: null,
    past4yearsPayerPrefectureCd: null,
    past4yearsPayerAddressCity: null,
    past4yearsPayerAddressOthers: null,
    past4yearsPayerName: null,
    retirementFormApplyId: null,
    page1Kbn: null,
    page1PaymentAmt: null,
    page1WithholdingAmt: null,
    page1ScAmtCity: null,
    page1ScAmtPrefecture: null,
    page1LengthOfService: null,
    page1EmployedDate: null,
    page1DeductionPeriodYear: null,
    page1DeductionPeriodMonth: null,
    page1RetiredDate: null,
    page2Kbn: null,
    page2PaymentAmt: null,
    page2WithholdingAmt: null,
    page2ScAmtCity: null,
    page2ScAmtPrefecture: null,
    page2LengthOfService: null,
    page2EmployedDate: null,
    page2RetiredDate: null,
    page2DeductionPeriodYear: null,
    page2DeductionPeriodMonth: null,
    page3Kbn: null,
    page3PaymentAmt: null,
    page3WithholdingAmt: null,
    page3ScAmtCity: null,
    page3ScAmtPrefecture: null,
    page3LengthOfService: null,
    page3EmployedDate: null,
    page3RetiredDate: null,
    page3DeductionPeriodYear: null,
    page3DeductionPeriodMonth: null,
    contractorName: null,
    fileList: [],
    receptionDate: null,
    screenId: null,
  };

  const finalRes = assignValuesFromObject(MapValue, tempParam);

  return finalRes as IAPI_RET005_Request;
}

// 受領者変更更新
function API_RET018_BuildReqParams(
  param: TCombinedType<IAPI_RET018_Request, IJob_40Props>
): IAPI_RET018_Request {
  const tempParam = removeUndefinedProperties<
    IAPI_RET018_Request,
    IJob_40Props
  >(param);
  const MapValue = {
    applyId: 0,
    tempSaveStep: 0,
    contractorNo: "",
    staffNo: "",
    demanderName: "",
    demanderNameKana: "",
    demanderKbn: "",
    demanderRelationship: "",
    demanderRelationshipOthers: "",
    demanderBirth: "",
    tel: "",
    demanderEmail: "",
    agentEmail: "",
    demanderZipcode: "",
    demanderPrefectureCd: "",
    demanderCity: "",
    demanderAddressOthers: "",
    demanderFlg: "",
    livelihoodFromRetireeFlg: "",
    fileList: [],
    approvalDate: "",
    screenId: "",
  };

  const finalRes = assignValuesFromObject(MapValue, tempParam);

  return finalRes as IAPI_RET018_Request;
}

export {
  API_STA008_BuildReqParams,
  API_RET005_BuildReqParams,
  API_RET018_BuildReqParams,
  monthsList,
};
